import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useParams,
} from "react-router-dom";
import QuoteSection from "../../components/contactform/QuoteSection";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000"
).replace(/\/+$/, "");

function OurService() {
  const { categorySlug } = useParams();
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /*
  |--------------------------------------------------------------------------
  | Dil
  |--------------------------------------------------------------------------
  */
  const currentLang = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return params.get("lang") || "tr";
  }, [location.search]);

  /*
  |--------------------------------------------------------------------------
  | Türkçe slug
  |--------------------------------------------------------------------------
  */
  const createSlug = (text = "") => {
    return String(text)
      .trim()
      .toLocaleLowerCase("tr-TR")
      .replace(/ç/g, "c")
      .replace(/ğ/g, "g")
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ş/g, "s")
      .replace(/ü/g, "u")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  /*
  |--------------------------------------------------------------------------
  | Slug sonundaki ID'yi al
  |--------------------------------------------------------------------------
  |
  | galvaniz-kapli-tel-24
  | ->
  | 24
  |
  */
  const getIdFromSlug = (slug = "") => {
    const match = String(slug).match(/-(\d+)$/);

    if (!match) {
      return null;
    }

    return Number(match[1]);
  };

  const categoryId = categorySlug
    ? getIdFromSlug(categorySlug)
    : null;

  /*
  |--------------------------------------------------------------------------
  | Linklerde dili koru
  |--------------------------------------------------------------------------
  */
  const withLang = (path) => {
    if (!currentLang || currentLang === "tr") {
      return path;
    }

    return `${path}?lang=${encodeURIComponent(
      currentLang
    )}`;
  };

  /*
  |--------------------------------------------------------------------------
  | CDN / Görsel URL
  |--------------------------------------------------------------------------
  */
  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    let imagePath = image;

    if (typeof image === "object") {
      if (Array.isArray(image)) {
        imagePath =
          image[0]?.url ||
          image[0]?.path ||
          image[0]?.src ||
          "";
      } else {
        imagePath =
          image.url ||
          image.path ||
          image.src ||
          "";
      }
    }

    if (
      !imagePath ||
      typeof imagePath !== "string"
    ) {
      return "";
    }

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://") ||
      imagePath.startsWith("data:") ||
      imagePath.startsWith("blob:")
    ) {
      return imagePath;
    }

    if (imagePath.startsWith("/cdn/")) {
      return `${API_BASE_URL}${imagePath}`;
    }

    const cleanImagePath = imagePath.replace(
      /^\/+/,
      ""
    );

    return `${API_BASE_URL}/cdn/${cleanImagePath}`;
  };

  /*
  |--------------------------------------------------------------------------
  | Kategorileri + gerekiyorsa kategori ürünlerini getir
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        /*
         * 1) Tüm kategoriler
         */
        const categoriesResponse = await fetch(
          `${API_BASE_URL}/api/blogs/products/categories?lang=${encodeURIComponent(
            currentLang
          )}`,
          {
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        if (!categoriesResponse.ok) {
          throw new Error(
            `Kategoriler alınamadı: ${categoriesResponse.status}`
          );
        }

        const categoryData =
          await categoriesResponse.json();

        const categoryList = Array.isArray(
          categoryData
        )
          ? categoryData
          : [];

        setCategories(categoryList);

        /*
         * /our-service ise sadece kategoriler gösterilecek.
         */
        if (!categorySlug) {
          setProducts([]);
          return;
        }

        /*
         * Hatalı URL
         */
        if (!categoryId) {
          setProducts([]);
          setError("Kategori bulunamadı.");
          return;
        }

        /*
         * 2) Seçilen kategorinin ürünleri
         */
        const productsResponse = await fetch(
          `${API_BASE_URL}/api/blogs/products?lang=${encodeURIComponent(
            currentLang
          )}&category=${categoryId}`,
          {
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        if (!productsResponse.ok) {
          throw new Error(
            `Ürünler alınamadı: ${productsResponse.status}`
          );
        }

        const productData =
          await productsResponse.json();

        setProducts(
          Array.isArray(productData)
            ? productData
            : []
        );
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(
            "PRODUCT_LIST_ERROR:",
            err
          );

          setError(
            "Ürünler yüklenirken bir hata oluştu."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [
    currentLang,
    categorySlug,
    categoryId,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Aktif kategori
  |--------------------------------------------------------------------------
  */
  const activeCategory = categories.find(
    (category) =>
      Number(category.id) === Number(categoryId)
  );

  /*
  |--------------------------------------------------------------------------
  | Sayfa başlığı
  |--------------------------------------------------------------------------
  */
  const pageTitle = categorySlug
    ? activeCategory?.title ||
      activeCategory?.name ||
      "Ürünler"
    : "Ürünler ve Müşterilerimizin Uygulamaları ";

  const breadcrumbs = [
    {
      label: "Ana Sayfa",
      link: withLang("/"),
    },
    {
      label: "Ürünler ve Müşterilerimizin Uygulamaları",
      link: categorySlug
        ? withLang("/our-service")
        : undefined,
    },
    ...(categorySlug
      ? [
          {
            label: pageTitle,
          },
        ]
      : []),
  ];

  return (
    <div>


      <style>
        {`
          .demirsan-service-list {
            padding: 30px 0 30px;
          }

          .demirsan-service-heading {
            max-width: 760px;
            margin: 0 auto 50px;
            text-align: center;
          }

          .demirsan-service-heading h2 {
            margin-bottom: 15px;
          }

          .demirsan-service-heading p {
            margin: 0;
            color: #666;
            line-height: 1.75;
          }

          .demirsan-product-card {
            display: flex;
            flex-direction: column;
            height: 100%;
            background: #fff;
            overflow: hidden;
            box-shadow:
              0 10px 35px rgba(0,0,0,.08);
            transition:
              transform .25s ease,
              box-shadow .25s ease;
          }

          .demirsan-product-card:hover {
            transform: translateY(-5px);
            box-shadow:
              0 18px 45px rgba(0,0,0,.12);
          }

          .demirsan-product-image {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 320px;
            padding: 22px;
            overflow: hidden;
            background: #f7f7f7;
          }

          .demirsan-product-image img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }

          .demirsan-product-no-image {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            color: #999;
            font-size: 14px;
          }

          .demirsan-product-content {
            display: flex;
            flex-direction: column;
            flex: 1;
            padding: 28px 30px 30px;
          }

          .demirsan-product-content h4 {
            margin: 0 0 13px;
            color: #111;
            font-size: 22px;
            line-height: 1.35;
          }

          .demirsan-product-content p {
            margin: 0 0 22px;
            color: #666;
            font-size: 14px;
            line-height: 1.7;
          }

          .demirsan-product-link {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin-top: auto;
            color: #e20614;
            font-weight: 600;
          }

          .demirsan-product-link:hover {
            color: #b8000c;
          }

          .demirsan-back-category {
            margin-bottom: 30px;
          }

          .demirsan-back-category a {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            color: #e20614;
            font-weight: 600;
          }

          .demirsan-service-loading,
          .demirsan-service-empty {
            width: 100%;
            padding: 60px 20px;
            text-align: center;
          }

          @media (max-width: 767px) {
            .demirsan-service-list {
              padding: 60px 0 75px;
            }

            .demirsan-product-image {
              height: 280px;
            }
          }
        `}
      </style>

      <section className="demirsan-service-list">
        <div className="container">

          {categorySlug && (
            <div className="demirsan-back-category">
              <Link
                to={withLang("/our-service")}
              >
                <i className="far fa-arrow-left" />
                Tüm Kategoriler
              </Link>
            </div>
          )}

          <div className="demirsan-service-heading">
            <h2>{pageTitle}</h2>

            {categorySlug &&
              activeCategory?.description && (
                <p>
                  {activeCategory.description}
                </p>
              )}

            {!categorySlug && (
              <p>
                Ürün gruplarımızı inceleyerek
                ihtiyacınıza uygun ürün ve
                uygulamalara ulaşabilirsiniz.
              </p>
            )}
          </div>

          {loading ? (
            <div className="demirsan-service-loading">
              Yükleniyor...
            </div>
          ) : error ? (
            <div className="demirsan-service-empty">
              {error}
            </div>
          ) : (
            <div className="row g-5">

              {/* ==========================================
                  KATEGORİ YOKSA TÜM KATEGORİLER
              =========================================== */}
              {!categorySlug &&
                categories.map((category) => {
                  const title =
                    category.title ||
                    category.name ||
                    "Kategori";

                  const slug =
                    `${createSlug(title)}-${category.id}`;

                  const image = getImageUrl(
                    category.picture
                  );

                  return (
                    <div
                      key={category.id}
                      className="col-xl-4 col-md-6 col-12"
                    >
                      <Link
                        to={withLang(
                          `/our-service/${slug}`
                        )}
                        style={{
                          display: "block",
                          height: "100%",
                        }}
                      >
                        <div className="demirsan-product-card">

                          <div className="demirsan-product-image">
                            {image ? (
                              <img
                                src={image}
                                alt={title}
                              />
                            ) : (
                              <div className="demirsan-product-no-image">
                                Görsel bulunmuyor
                              </div>
                            )}
                          </div>

                          <div className="demirsan-product-content">
                            <h4>{title}</h4>

                            {category.description && (
                              <p>
                                {category.description}
                              </p>
                            )}

                            <span className="demirsan-product-link">
                              Ürünleri İncele
                              <i className="far fa-arrow-right" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}

              {/* ==========================================
                  KATEGORİ SEÇİLDİYSE ÜRÜNLER
              =========================================== */}
              {categorySlug &&
                products.map((product) => {
                  const title =
                    product.title ||
                    product.data?.title ||
                    "Ürün";

                  const slug =
                    `${createSlug(title)}-${product.id}`;

                  const image = getImageUrl(
                    product.image ||
                    product.data?.image ||
                    product.data?.computerImage
                  );

                  return (
                    <div
                      key={product.id}
                      className="col-xl-4 col-md-6 col-12"
                    >
                      <Link
                        to={withLang(
                          `/service-details/${slug}`
                        )}
                        style={{
                          display: "block",
                          height: "100%",
                        }}
                      >
                        <div className="demirsan-product-card">

                          <div className="demirsan-product-image">
                            {image ? (
                              <img
                                src={image}
                                alt={title}
                              />
                            ) : (
                              <div className="demirsan-product-no-image">
                                Görsel bulunmuyor
                              </div>
                            )}
                          </div>

                          <div className="demirsan-product-content">
                            <h4>{title}</h4>

                            {(product.shortDescription ||
                              product.description) && (
                              <p>
                                {product.shortDescription ||
                                  product.description}
                              </p>
                            )}

                            <span className="demirsan-product-link">
                              Ürünü İncele
                              <i className="far fa-arrow-right" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}

              {categorySlug &&
                products.length === 0 && (
                  <div className="demirsan-service-empty">
                    Bu kategoride henüz ürün
                    bulunmuyor.
                  </div>
                )}

            </div>
          )}
        </div>
      </section>

      <QuoteSection />
    </div>
  );
}

export default OurService;